import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { api, errorMessage, launchUrl, type DefaultTarget, type Instance, type Me } from '../api';
import { Alert } from '../components/Alert';
import { InstanceCard } from '../components/InstanceCard';
import { LaunchChoice } from '../components/LaunchChoice';
import { useInterval } from '../hooks';
import { useLocation } from '../router';

const DEFAULT_PORT = 3081;

/** Une instance par type : la plus pertinente (non retirée de préférence, puis la plus récente). */
export function pickInstances(list: Instance[]): { cloud: Instance | null; local: Instance | null } {
  const pick = (kind: Instance['kind']): Instance | null => {
    const items = list
      .filter((i) => i.kind === kind)
      .sort((a, b) => {
        const ra = a.status === 'removed' ? 1 : 0;
        const rb = b.status === 'removed' ? 1 : 0;
        return ra - rb || b.createdAt.localeCompare(a.createdAt);
      });
    return items[0] ?? null;
  };
  return { cloud: pick('cloud'), local: pick('local') };
}

interface Props {
  me: Me;
  onPreferenceChange?: (t: DefaultTarget) => void;
}

export function Home({ me, onPreferenceChange }: Props) {
  const [instances, setInstances] = useState<Instance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { search } = useLocation();
  const prefId = useId();
  const portId = useId();

  const load = useCallback(async () => {
    try {
      setInstances(await api.instances());
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { cloud, local } = pickInstances(instances ?? []);
  const provisioning = (instances ?? []).some((i) => i.status === 'provisioning');
  useInterval(() => void load(), provisioning ? 5000 : null);

  const cloudReady = cloud?.status === 'ready';
  const localReady = local?.status === 'ready';
  const showChoice = cloudReady && localReady && (search.get('choose') === '1' || me.defaultTarget === 'ask');
  const anyReady = cloudReady || localReady;

  const [port, setPort] = useState<string>(String(DEFAULT_PORT));
  const [portTouched, setPortTouched] = useState(false);
  const [savingPort, setSavingPort] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [portError, setPortError] = useState<string | null>(null);

  useEffect(() => {
    if (!portTouched && local?.localPort) setPort(String(local.localPort));
  }, [local?.localPort, portTouched]);

  async function savePort(e: FormEvent) {
    e.preventDefault();
    const n = Number(port);
    if (!Number.isInteger(n) || n < 1024 || n > 65535) {
      setPortError('Le port doit être un entier entre 1024 et 65535.');
      return;
    }
    setPortError(null);
    setSavingPort(true);
    try {
      await api.setLocalInstance(n);
      setNotice(`Instance locale déclarée sur le port ${n}.`);
      setPortTouched(false);
      await load();
    } catch (err) {
      setPortError(errorMessage(err));
    } finally {
      setSavingPort(false);
    }
  }

  async function deleteLocal() {
    setSavingPort(true);
    try {
      await api.deleteLocalInstance();
      setNotice('Instance locale supprimée.');
      setConfirmDelete(false);
      setPortTouched(false);
      setPort(String(DEFAULT_PORT));
      await load();
    } catch (err) {
      setPortError(errorMessage(err));
    } finally {
      setSavingPort(false);
    }
  }

  async function changePreference(value: DefaultTarget) {
    const previous = me.defaultTarget;
    onPreferenceChange?.(value);
    try {
      const r = await api.setPreference(value);
      onPreferenceChange?.(r.defaultTarget);
    } catch (err) {
      onPreferenceChange?.(previous);
      setError(errorMessage(err));
    }
  }

  const hasLocalDeclared = !!local && local.status !== 'removed';

  return (
    <div className="stack">
      <div className="page-head">
        <h1>Mes instances</h1>
        <div className="page-head-actions">
          {anyReady ? (
            <a className="btn btn-primary btn-big" href={launchUrl()}>
              Ouvrir WH
            </a>
          ) : (
            <button type="button" className="btn btn-primary btn-big" disabled>
              Ouvrir WH
            </button>
          )}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}

      {showChoice && <LaunchChoice />}

      {instances === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des instances…
        </p>
      )}

      {instances !== null && cloud === null && local === null && (
        <section className="empty" aria-labelledby="empty-title">
          <h2 id="empty-title">Aucune instance pour le moment</h2>
          <p>Demandez à un administrateur de créer votre instance cloud.</p>
          <p className="muted">
            Vous pouvez aussi déclarer une instance WH locale ci-dessous si elle tourne déjà sur votre poste.
          </p>
        </section>
      )}

      {(cloud || local) && (
        <section aria-label="Instances" className="grid">
          {cloud && <InstanceCard instance={cloud} />}
          {local && <InstanceCard instance={local} />}
        </section>
      )}

      <section className="panel" aria-labelledby={`${prefId}-t`}>
        <h2 id={`${prefId}-t`}>Préférence d'ouverture</h2>
        <div className="field">
          <label htmlFor={prefId}>Instance ouverte par défaut</label>
          <select
            id={prefId}
            value={me.defaultTarget}
            onChange={(e) => void changePreference(e.target.value as DefaultTarget)}
          >
            <option value="ask">Me demander</option>
            <option value="cloud">Toujours cloud</option>
            <option value="local">Toujours local</option>
          </select>
        </div>
      </section>

      <section className="panel" aria-labelledby={`${portId}-t`}>
        <h2 id={`${portId}-t`}>Instance locale</h2>
        <p className="muted">
          Déclarez le port sur lequel votre WH local écoute (http://127.0.0.1:&lt;port&gt;/).
        </p>
        <form className="inline-form" onSubmit={savePort} noValidate>
          <div className="field">
            <label htmlFor={portId}>Port local</label>
            <input
              id={portId}
              type="number"
              inputMode="numeric"
              min={1024}
              max={65535}
              value={port}
              onChange={(e) => {
                setPort(e.target.value);
                setPortTouched(true);
              }}
              aria-invalid={portError ? true : undefined}
              aria-describedby={portError ? `${portId}-err` : undefined}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingPort}>
            {hasLocalDeclared ? 'Mettre à jour' : 'Déclarer'}
          </button>
          {hasLocalDeclared &&
            (confirmDelete ? (
              <span className="confirm-inline" role="group" aria-label="Confirmer la suppression">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void deleteLocal()}
                  disabled={savingPort}
                >
                  Confirmer la suppression
                </button>
                <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
                  Annuler
                </button>
              </span>
            ) : (
              <button type="button" className="btn" onClick={() => setConfirmDelete(true)}>
                Supprimer
              </button>
            ))}
        </form>
        {portError && (
          <p id={`${portId}-err`} className="field-error" role="alert">
            {portError}
          </p>
        )}
      </section>
    </div>
  );
}
